/* Copyright (c) 2025 Xperi. All Rights Reserved. */

/**
 * This file implements all the functions for VOD feature,
 * which are exposed from the data/domain layer to other layers.
 * Any modifications to the exposed functions can be made here
 */
import { ContentScreenDetails } from 'client-code/actions';
import { SourceType } from 'client-code/channels-service';
import { MayAlsoLikeItems } from 'client-code/feeditems-service';
import { Credit } from 'client-code/person-content-service';
import { PreviewGroup, ScreenType } from 'client-code/previews';
import { VodLaunchDetails, VodPurchaseStatus } from 'client-code/vod-browse';

import {
    DEFAULT_RETRY_OPTIONS,
    IDLE_GRID_GUIDE_DURATION,
    MILLISECONDS_IN_A_SECOND,
    TIVO_SERVICES,
} from '../../config';
import { AppUtils } from '../AppUtils';
import { DeviceFeatures, DeviceModel } from '../device';
import { DimensionScaler } from '../device/DimensionScaler';
import { OneLineGuideCell, OneLineGuideRow } from '../entities';
import { ErrorTypes, TypedChannelsNotFoundError } from '../error';
import {
    ActionDetailsService,
    ActionsService,
    ChannelsService,
    FeedItemsService,
    GridGuideService,
    PersonContentService,
    PreviewsService,
    Services,
    VodBrowseService,
    VodSocuLastViewedPositionService,
} from '../infrastructure';
import { Locale, LocaleKeys } from '../locale';
import { Log } from '../logging';
import { PlaybackContentType } from '../playback/enums';
import { RetryMechanism } from '../retrymechanism/RetryMechanism';
import { RetryOptions } from '../retrymechanism/RetryOptions';
import { OLG_EPISODIC_POSTER_IMAGE_HEIGHT, OLG_EPISODIC_POSTER_IMAGE_WIDTH } from '../strings';
import {
    TActionDetailsList,
    TActionDetailsParamsUnifiedCollection,
    TActionsContentScreenDetailsContentIdParamsV2,
    TChannelsV2,
    TChannelV2,
    TContentPreview,
    TGridGuideRow,
    TGuideCell,
    TOneLineGuideRow,
    TVodBrowseData,
    TVodSocuLastViewedPositionParams,
} from '../types';
import { ChannelsType } from '../types/TChannelsType';
import { TVodOfferPreview } from '../types/TVodOfferPreview';
import { TVodOfferPreviewCustomData } from '../types/TVodOfferPreviewCustomData';
import { DateTime, UniqueIdentifier } from '../utils';
import { IPlayable } from '../videoplayer/interfaces';
import { ServicesClientImpl } from './ServicesClientImpl';
import { ONE_LINE_GUIDE_CHANNEL_SLICE_SIZE } from '../../store/videoplayerui/onelineguide/onelineguide.constants';

export class VodInteractor extends ServicesClientImpl {
    // eslint-disable-next-line class-methods-use-this
    private _commonParameters() {
        return {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        };
    }

    private _commonParametersWithTimeZone() {
        return {
            ...this._commonParameters(),
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
        };
    }

    /**
     * Fetches a list of VOD items for browsing.
     *
     * @param parentVodItemId (string)- The ID of the parent VOD item to fetch children for.
     * @param includeAdult (boolean)- Whether to include adult content in the results.
     * @param offset (number)- The index of the first item in the response.
     * @param limit (number)- Limit the response items to the given count.
     * @returns TVodBrowseData { VodItemList & totalCount}
     */

    // eslint-disable-next-line class-methods-use-this
    public getVodBrowseData(
        vodBrowseItemsParams: {
            parentVodItemId?: string;
            includeAdult?: boolean;
            offset?: number;
            limit?: number;
        } = {},
    ): Promise<TVodBrowseData> {
        const shouldHideMetadata =
            vodBrowseItemsParams.includeAdult === undefined
                ? !this.shouldHideMetadata()
                : vodBrowseItemsParams.includeAdult;
        const params = {
            ...vodBrowseItemsParams,
            includeAdult: shouldHideMetadata,
        };
        return Services.getInstanceOf(VodBrowseService, {
            ...this._commonParametersWithTimeZone(),
            basePath: VodInteractor.servicesBaseUrls[TIVO_SERVICES.VodBrowseService],
        }).getVodBrowseData(params);
    }

    public async getUnifiedCollectionActionDetails(
        unifiedCollectionParams: TActionDetailsParamsUnifiedCollection,
    ): Promise<TActionDetailsList> {
        return Services.getInstanceOf(ActionDetailsService, {
            ...this._commonParametersWithTimeZone(),
            basePath: VodInteractor.servicesBaseUrls[TIVO_SERVICES.ActionDetailsService],
        }).fetchUnifiedCollectionActionDetails(unifiedCollectionParams);
    }

    /**
     * The method will return the object `ContentScreenDetails` to fetch all 'content' related
     * actions
     * for the existing `recordingId`.
     * @param contentId (string) - The `contentId of the <episode/movie/game/match/event>
     * @param networkId (string) - [Optional Parameter]
     * @returns `ContentScreenDetails`
     */
    public getContentScreenDetailsContentActionsV2(
        actionsParams: TActionsContentScreenDetailsContentIdParamsV2,
    ): Promise<ContentScreenDetails> {
        return Services.getInstanceOf(ActionsService, {
            ...this._commonParametersWithTimeZone(),
            basePath: VodInteractor.servicesBaseUrls[TIVO_SERVICES.ActionsService],
        }).getContentScreenDetailsContentActionsV2(actionsParams);
    }

    /**
     * Below are the method related to cloudcore-previews
     */
    public fetchContentPreviewV3 = (contentPreviewParams: {
        contentId: string;
        partnerId?: string;
        collapsedVodItemId?: string;
        groups?: Array<PreviewGroup>;
        contentSupplierPartnerId?: string;
    }): Promise<TContentPreview> => {
        return Services.getInstanceOf(PreviewsService, {
            ...this._commonParametersWithTimeZone(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
        }).fetchContentPreviewV3(contentPreviewParams);
    };

    public fetchVodOfferPreviewV2 = (vodOfferPreviewParams: {
        vodOfferId: string;
        screenType: ScreenType;
        groups?: Array<PreviewGroup>;
        networkCallId?: number;
    }): Promise<TVodOfferPreview> => {
        return Services.getInstanceOf(PreviewsService, {
            ...this._commonParametersWithTimeZone(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
        }).fetchVodOfferPreviewV2(vodOfferPreviewParams);
    };

    public fetchVodFullPreview = async (
        offerId: string,
        screenType: ScreenType,
        groups: Array<PreviewGroup>,
        standardBannerResponse?: TVodOfferPreviewCustomData,
    ): Promise<TVodOfferPreviewCustomData> => {
        if (screenType === ScreenType.StandardBanner) {
            return (
                await this.fetchVodOfferPreviewV2({
                    vodOfferId: offerId,
                    screenType: ScreenType.StandardBanner,
                    groups,
                    networkCallId: UniqueIdentifier.getNetworkCallId(),
                })
            ).vodOfferPreview;
        }

        if (!Object.keys(standardBannerResponse || {}).length) {
            const standardBannerData = await this.fetchVodOfferPreviewV2({
                vodOfferId: offerId,
                screenType: ScreenType.StandardBanner,
                groups,
                networkCallId: UniqueIdentifier.getNetworkCallId(),
            });
            const fullBannerData = await this.fetchVodOfferPreviewV2({
                vodOfferId: offerId,
                screenType: ScreenType.FullBanner,
                groups,
                networkCallId: UniqueIdentifier.getNetworkCallId(),
            });
            return AppUtils.mergeObjects<TVodOfferPreviewCustomData>(
                standardBannerData.vodOfferPreview,
                fullBannerData.vodOfferPreview,
            );
        }

        const fullBannerResponse = await this.fetchVodOfferPreviewV2({
            vodOfferId: offerId,
            screenType: ScreenType.FullBanner,
            groups,
            networkCallId: UniqueIdentifier.getNetworkCallId(),
        });
        return AppUtils.mergeObjects<TVodOfferPreviewCustomData>(
            standardBannerResponse,
            fullBannerResponse.vodOfferPreview,
        );
    };

    public async purchaseVodItem(vodPurchaseParams: {
        applicationFeatureArea: string;
        partnerOfferId: string;
        price?: string;
    }): Promise<VodPurchaseStatus> {
        return Services.getInstanceOf(VodBrowseService, {
            ...this._commonParametersWithTimeZone(),
            basePath: VodInteractor.servicesBaseUrls[TIVO_SERVICES.VodBrowseService],
        }).purchaseVodItem(vodPurchaseParams);
    }

    public async setVodLastViewedPosition(
        vodLastViewedPositionParams: TVodSocuLastViewedPositionParams,
    ): Promise<void> {
        return Services.getInstanceOf(VodSocuLastViewedPositionService, {
            ...this._commonParameters(),
            basePath:
                VodInteractor.servicesBaseUrls[TIVO_SERVICES.VodSocuLastViewedPositionService],
        }).setVodLastViewedPosition(vodLastViewedPositionParams);
    }

    public async getVodLaunchInfo(vodJumpChannelNumber: string): Promise<VodLaunchDetails> {
        return Services.getInstanceOf(VodBrowseService, {
            ...this._commonParametersWithTimeZone(),
            basePath: VodInteractor.servicesBaseUrls[TIVO_SERVICES.VodBrowseService],
        }).getVodLaunchInfo(vodJumpChannelNumber);
    }

    // ====ChannelsServiceV2====
    public fetchChannelsV2 = async (
        channelType?: string,
        offset?: number,
        limit?: number,
    ): Promise<TChannelsV2> => {
        const service = Services.getInstanceOf(ChannelsService, this.getChannelsServiceParams());
        let data: TChannelsV2;
        switch (channelType) {
            case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS):
                data = await service.fetchMyChannelsV2(offset, limit);
                break;
            case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES):
                data = await service.fetchFavoriteChannelsV2(offset, limit);
                break;
            case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_ALL_CHANNELS):
            default:
                data = await service.fetchAllChannelsV2(offset, limit);
                break;
        }

        if (!data.channels.length)
            throw new TypedChannelsNotFoundError(
                ErrorTypes.GUIDE_CHANNELS_SERVICE_EMPTY_RESPONSE_C1106,
                this.formatErrorDescription(
                    Locale.getString(LocaleKeys.GUIDE_TEMPORARY_GUIDE_ISSUE_OVERLAY_DESCRIPTION),
                ),
            );
        return data;
    };

    public fetchAllChannelsV2 = (offset?: number, limit?: number): Promise<TChannelsV2> => {
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).fetchAllChannelsV2(offset, limit);
    };

    public fetchMyChannelsV2 = (offset?: number, limit?: number): Promise<TChannelsV2> => {
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).fetchMyChannelsV2(offset, limit);
    };

    public fetchNextChannelsFromChannelNumberV2 = (
        channelNumber: string,
        channelsType: string,
        limit: number,
    ): Promise<TChannelsV2> => {
        let mChannelsType: ChannelsType;

        switch (channelsType) {
            case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_ALL_CHANNELS):
                mChannelsType = ChannelsType.ALL;
                break;
            case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES):
                mChannelsType = ChannelsType.FAVORITE;
                break;
            case Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS):
            default:
                mChannelsType = ChannelsType.SUBSCRIBED;
        }
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).fetchNextChannelsFromChannelNumberV2(channelNumber, mChannelsType, limit);
    };

    public getChannelFromChannelNumberV2 = (channelNumber: string): Promise<TChannelV2> => {
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).tuneToChannelV2(channelNumber);
    };

    public getLastPlayedOrFallbackToFirstByChannelNumberV2 = async (
        channelNumber: string | null,
    ): Promise<IPlayable> => {
        let channel: TChannelV2;
        try {
            if (channelNumber === null) throw new Error('Last tuned channel not found.');
            channel = await this.getChannelFromChannelNumberV2(channelNumber);
        } catch (error) {
            Log.e(
                this.constructor.name,
                'getLastPlayedOrFallbackToFirstByChannelNumberV2()->Failed to get channel from channel number:',
                error,
            );
            const channels: TChannelsV2 = await this.fetchMyChannelsV2(0, 1);
            channel = channels?.channels[0];
        }
        return { channel, playbackContentType: PlaybackContentType.LIVE };
    };

    public loadChannelsInfoV2 = (): Promise<boolean> => {
        const fetchAllChannelsV2 = this.fetchAllChannelsV2.bind(this);
        return new Promise((resolve, reject) => {
            RetryMechanism.callServiceWithRetry(
                fetchAllChannelsV2,
                [],
                <RetryOptions>DEFAULT_RETRY_OPTIONS,
            )
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error instanceof Error ? error : new Error(String(error)));
                });
        });
    };

    public getNextChannelV2 = (
        channelNumber: string,
        channelsType: string,
        count?: number,
    ): Promise<TChannelV2> => {
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).getNextChannelV2(channelNumber, channelsType, count);
    };

    public getPreviousChannelV2 = (
        channelNumber: string,
        channelsType: string,
        count?: number,
    ): Promise<TChannelV2> => {
        return Services.getInstanceOf(
            ChannelsService,
            this.getChannelsServiceParams(),
        ).getPreviousChannelV2(channelNumber, channelsType, count);
    };

    // ====ChannelsServiceV2 End====

    // ====GridGuideServiceV2====
    public fetchProgramMetadataV2 = (
        stationId: string[],
        startTime: DateTime,
        endTime: DateTime,
    ): Promise<TGridGuideRow[]> => {
        return Services.getInstanceOf(GridGuideService, {
            ...this._commonParameters(),
            basePath: VodInteractor.servicesBaseUrls[TIVO_SERVICES.GridGuideService],
        }).fetchProgramMetadataV2(stationId, startTime, endTime);
    };

    private createDummyOLGCellsForVod = (
        pastMode: boolean,
        anchorTime: DateTime,
        posterImageUrl: string,
        channel: TChannelV2,
    ) => {
        const cells: OneLineGuideCell[] = [];

        for (let i = 0; i < ONE_LINE_GUIDE_CHANNEL_SLICE_SIZE; i += 1) {
            const offsetMinutes = 30 * i;

            const cellStart = pastMode
                ? anchorTime.subtractMinutes(30 + offsetMinutes)
                : anchorTime.addMinutes(offsetMinutes);

            cells.push({
                collectionId: '',
                contentId: '',
                duration: 1800,
                offerId: UniqueIdentifier.uuid4(),
                posterImageUrl,
                startTime: cellStart,
                title: channel.jumpChannelInfo?.metadata.guideCellTitle ?? '',
            });
        }

        return cells;
    };

    public fetchOneLineGuideRowMetadataV2 = async (
        channel: TChannelV2,
        anchorTime: DateTime,
        count: number,
        offset: number,
        pastMode: boolean,
        networkCallId?: number,
    ): Promise<TOneLineGuideRow> => {
        if (channel?.sourceType === SourceType.JumpChannel) {
            return new Promise((resolve) => {
                const imageHostBaseUrl = VodInteractor.servicesBaseUrls[TIVO_SERVICES.ImageHost];
                const objectid = channel?.jumpChannelInfo?.metadata?.logoPartnerId;
                const { width, height } = DimensionScaler.getInstance().getScaledDimensions(
                    OLG_EPISODIC_POSTER_IMAGE_WIDTH,
                    OLG_EPISODIC_POSTER_IMAGE_HEIGHT,
                );
                const posterImageUrl = `${imageHostBaseUrl}/images?objectid=${objectid}&imagetype=partnerSourceLogo&width=${width}&height=${height}`;
                const OLGCells: OneLineGuideCell[] = this.createDummyOLGCellsForVod(
                    pastMode,
                    anchorTime,
                    posterImageUrl,
                    channel,
                );
                const OLGRow: OneLineGuideRow = {
                    oneLineGuideRow: OLGCells,
                    networkCallId,
                };
                resolve(OLGRow);
            });
        }

        const { stationId } = channel;
        if (!stationId) {
            return Promise.reject(new Error('Station ID is missing for the channel'));
        }

        return Services.getInstanceOf(GridGuideService, {
            ...this._commonParameters(),
            basePath: VodInteractor.servicesBaseUrls[TIVO_SERVICES.GridGuideService],
        }).fetchOneLineGuideRowMetadataV2(
            stationId,
            anchorTime,
            count,
            offset,
            pastMode,
            networkCallId,
        );
    };

    public fetchGuideRowsDataV2 = (
        channels: TChannelV2[],
        startTime: DateTime,
        endTime: DateTime,
        guideStartTime?: DateTime,
    ): Promise<TGridGuideRow[]> => {
        const jumpChannels = new Map<number, TChannelV2>();
        const stationIdsWithPossibleJumpChannel: string[] = channels.map((channel, index) => {
            if (channel.sourceType === SourceType.JumpChannel) {
                jumpChannels.set(index, channel);
                return SourceType.JumpChannel;
            }
            return channel.stationId ?? '';
        });
        const stationIds: string[] = stationIdsWithPossibleJumpChannel.filter(
            (stationId) => stationId !== SourceType.JumpChannel && stationId !== '',
        );
        const _guideStartTime = guideStartTime || new DateTime();

        return new Promise((resolve, reject) => {
            this.fetchProgramMetadataV2(stationIds, startTime, endTime)
                .then((guideRows) => {
                    const mGuideRows = this.validateAndCreateGuideRowsForUI(
                        guideRows,
                        jumpChannels,
                        stationIdsWithPossibleJumpChannel,
                        startTime,
                        endTime,
                    );
                    resolve(mGuideRows);
                })
                .catch((error) => {
                    Log.e(this.constructor.name, error);

                    const dummyGuideRows = channels.map((channel) => {
                        return this.createDummyGuideRowDataForUI(
                            channel,
                            _guideStartTime,
                            _guideStartTime.addMinutes(IDLE_GRID_GUIDE_DURATION),
                        );
                    });

                    const modifiedError = { ...error, dummyGuideRows };
                    reject(modifiedError);
                });
        });
    };

    private validateAndCreateGuideRowsForUI(
        guideRows: TGridGuideRow[],
        jumpChannels: Map<number, TChannelV2>,
        stationIds: string[],
        startTime: DateTime,
        endTime: DateTime,
    ): TGridGuideRow[] {
        // creating map of guide rows response recieved
        const guideRowsMap = new Map<string, TGridGuideRow>();
        guideRows.forEach((guideRow) => {
            if (guideRow.stationId) {
                guideRowsMap.set(guideRow.stationId, guideRow);
            }
        });

        // Filling the guide rows as accordance with the requested station ids
        const filledGuideRows: TGridGuideRow[] = stationIds.map((stationId, index) => {
            // creating dummy guide row for jump channels
            if (stationId === SourceType.JumpChannel && jumpChannels.has(index)) {
                return <TGridGuideRow>{
                    stationId,
                    guideCells: [
                        this.createDummyGuideCell({
                            startTime: startTime.subtractMinutes(30),
                            endTime: endTime.addMinutes(30),
                            jumpChannel: jumpChannels.get(index),
                        }),
                    ],
                };
            }

            const guideRow = guideRowsMap.get(stationId);
            // if no guide row or no guide cells present for the station id, creating
            // dummy guide row for that station id
            if (!guideRow || guideRow.guideCells.length === 0) {
                return <TGridGuideRow>{
                    stationId,
                    guideCells: [this.createDummyGuideCell({ startTime, endTime })],
                };
            }

            const mGuideCells: TGuideCell[] = [];
            guideRow.guideCells.forEach((guideCell, idx): void => {
                const nextGuideCell = guideRow.guideCells[idx + 1];
                const currentGuideCellEndTime: DateTime = guideCell.startTime.addSeconds(
                    guideCell.duration,
                );

                // Leading gap before first cell
                if (idx === 0 && guideCell.startTime.getTime() > startTime.getTime()) {
                    mGuideCells.push(
                        this.createDummyGuideCell({ startTime, endTime: guideCell.startTime }),
                    );
                    mGuideCells.push(guideCell);

                    // If this is also the last cell, check trailing gap
                    if (
                        guideRow.guideCells.length === idx + 1 &&
                        currentGuideCellEndTime.getTime() < endTime.getTime()
                    ) {
                        mGuideCells.push(
                            this.createDummyGuideCell({
                                startTime: currentGuideCellEndTime,
                                endTime,
                            }),
                        );
                    }
                    return;
                }

                // Gap between this cell and the next cell
                if (
                    nextGuideCell &&
                    currentGuideCellEndTime.getTime() < nextGuideCell.startTime.getTime()
                ) {
                    mGuideCells.push(guideCell);
                    mGuideCells.push(
                        this.createDummyGuideCell({
                            startTime: currentGuideCellEndTime,
                            endTime: nextGuideCell.startTime,
                        }),
                    );
                    return;
                }

                // Trailing gap after the last cell
                if (
                    guideRow.guideCells.length === idx + 1 &&
                    currentGuideCellEndTime.getTime() < endTime.getTime()
                ) {
                    mGuideCells.push(guideCell);
                    mGuideCells.push(
                        this.createDummyGuideCell({
                            startTime: currentGuideCellEndTime,
                            endTime,
                        }),
                    );
                    return;
                }

                // Normal contiguous cell
                mGuideCells.push(guideCell);
            });
            return {
                stationId,
                guideCells: mGuideCells,
            };
        });

        return filledGuideRows;
    }

    private createDummyGuideRowDataForUI(
        channel: TChannelV2,
        startTime: DateTime,
        endTime: DateTime,
    ): TGridGuideRow {
        const { stationId, affiliate, callSign, videoResolution, sourceType } = channel;
        const cellTitle = `${Locale.getString(LocaleKeys.GUIDE_SELECT_TO_WATCH_TEXT)} ${
            affiliate?.split(' ')[0] || callSign
        } ${videoResolution?.toUpperCase()}`;
        const guideRow: TGridGuideRow = {
            stationId: stationId ?? '',
            guideCells: [
                this.createDummyGuideCell({
                    startTime,
                    endTime,
                    title: cellTitle,
                    jumpChannel: sourceType === SourceType.JumpChannel ? channel : undefined,
                }),
            ],
        };
        return guideRow;
    }

    // eslint-disable-next-line class-methods-use-this
    private createDummyGuideCell({
        startTime,
        endTime,
        jumpChannel,
        title = Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE),
    }: {
        startTime: DateTime;
        endTime: DateTime;
        jumpChannel?: TChannelV2;
        title?: string;
    }): TGuideCell {
        const cellTitle = jumpChannel
            ? jumpChannel.jumpChannelInfo?.metadata.guideCellTitle
            : title;
        const duration: number =
            (endTime.getTime() - startTime.getTime()) / MILLISECONDS_IN_A_SECOND;
        return {
            collectionId: '',
            contentId: '',
            duration,
            offerId: '',
            posterImageUrl: '',
            startTime,
            title: cellTitle,
        };
    }
    // ====GridGuideServiceV2 End====

    // ====FeedItemsService====
    public fetchContentMayAlsoLike = (
        contentId: string,
        offset?: number,
        count?: number,
        includeAdult?: boolean,
        includeIpVod?: boolean,
        includeSportsPass?: boolean,
        providerPartnerId?: string,
    ): Promise<MayAlsoLikeItems> => {
        return Services.getInstanceOf(FeedItemsService, {
            ...this._commonParameters(),
            basePath: VodInteractor.servicesBaseUrls[TIVO_SERVICES.FeedItemsService],
        }).fetchMayAlsoLikeContent(
            contentId,
            offset,
            count,
            includeAdult,
            includeIpVod,
            includeSportsPass,
            providerPartnerId,
        );
    };

    public fetchCollectionMayAlsoLike = (
        applicationFeatureArea: string,
        collectionId: string,
        offset?: number,
        count?: number,
        includeAdult?: boolean,
        includeIpVod?: boolean,
        includeSportsPass?: boolean,
        providerPartnerId?: string,
        isSeries?: boolean,
    ): Promise<MayAlsoLikeItems> => {
        return Services.getInstanceOf(FeedItemsService, {
            ...this._commonParameters(),
            basePath: VodInteractor.servicesBaseUrls[TIVO_SERVICES.FeedItemsService],
        }).fetchMayAlsoLikeCollection(
            applicationFeatureArea,
            collectionId,
            offset,
            count,
            includeAdult,
            includeIpVod,
            includeSportsPass,
            providerPartnerId,
            isSeries,
        );
    };
    // ====FeedItemsService End====

    public getCollectionCastCredits = (
        applicationFeatureArea: string,
        collectionId: string,
    ): Promise<Credit[]> => {
        return Services.getInstanceOf(PersonContentService, {
            ...this._commonParametersWithTimeZone(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PersonContentService],
        }).getCollectionCastCredits(applicationFeatureArea, collectionId);
    };

    // eslint-disable-next-line class-methods-use-this
    public clearPersonContentServiceCache(): void {
        PersonContentService.clearCache();
    }
}
